// Trang Khoe Thành Tích NGÀY — STT 15 (URL /khoe-thanh-tich).
// Số liệu doanh số lấy từ DailySalesAPI.getSummary → BE /orders/daily-summary.
//   sales.soLe = đơn LẺ (orders.type=1), sales.soSi = đơn SỈ (orders.type=2).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SystemHeader from '../SystemHeader';
import {
  DailyReportAPI, ReelsAutoCheckAPI, ReelTrendingAPI,
  LivestreamAPI, TeamMeetingAPI, MarketAPI, SpaMarketAPI,
  DailySalesAPI, ThongDiepAPI,
} from '../../services/feature-api';
import { Calendar, Shuffle, List, X, ChevronRight } from 'lucide-react';

const DAY_PALETTE = ['#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#10B981', '#EF4444', '#6366F1'];

const toDateStr = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const fmtDMY = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const fmtVNDFull = (n) => Number(n || 0).toLocaleString('vi-VN');
const fmtTime = (val) => { if (!val) return ''; try { const d = new Date(val); return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

const parseChecklist = (raw) => {
  if (!raw) return [];
  if (typeof raw === 'string') { try { const p = JSON.parse(raw); if (Array.isArray(p)) return p; } catch { return raw.split('\n').filter(Boolean).map((t) => ({ text: t, done: false })); } }
  return Array.isArray(raw) ? raw : [];
};

const itemDateStr = (item) => {
  if (!item) return '';
  if (item.date) return typeof item.date === 'string' && item.date.length >= 10 ? item.date.slice(0, 10) : toDateStr(item.date);
  if (item.time) return toDateStr(item.time);
  if (item.createdAt) return toDateStr(item.createdAt);
  return '';
};

// Ảnh với viền gradient glow
const GlowPhoto = ({ src, accent, className = 'w-full rounded-xl object-cover' }) => {
  if (!src) return null;
  return (
    <div style={{ padding: 2, background: `linear-gradient(135deg, ${accent}, #9333ea, ${accent}88)`, borderRadius: 14 }}>
      <img src={src} alt="" className={className} style={{ borderRadius: 12 }} onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
    </div>
  );
};

// Block container
const Block = ({ accent, label, children }) => (
  <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: '#0d1f35', boxShadow: `0 0 0 1px ${accent}44, 0 4px 20px #00000040` }}>
    <div className="px-4 py-2 flex items-center gap-2" style={{ background: `${accent}22`, borderBottom: `1px solid ${accent}44` }}>
      <div className="w-1.5 h-4 rounded-full" style={{ background: accent }} />
      <span className="text-xs font-black uppercase tracking-widest" style={{ color: accent }}>{label}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const ThongDiepModal = ({ visible, onClose, accent, groups, onPick }) => {
  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-md max-h-[85vh] rounded-t-3xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-black text-base flex items-center gap-2" style={{ color: accent }}>
            <List size={18} /> Chọn thông điệp
          </h3>
          <button onClick={onClose} className="text-gray-400"><X size={22} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {groups.map((g, gi) => (
            <div key={gi}>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1 mb-2">{g.nhomChuDe}</div>
              <div className="space-y-1.5">
                {(g.items || []).map((it) => (
                  <button key={it.id} onClick={() => onPick(it)}
                    className="w-full text-left text-sm bg-gray-50 hover:bg-gray-100 rounded-xl px-4 py-2.5 leading-relaxed font-medium text-gray-800">
                    "{it.cauTrietLy}"
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const KhoeThanhTichPage = ({ userInfo, accessToken, onLogout, totalPoints, onRefreshPoints }) => {
  const today = useMemo(() => toDateStr(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [checklistItems, setChecklistItems] = useState([]);
  const [sales, setSales] = useState(null);
  const [reelsChecked, setReelsChecked] = useState([]);
  const [reelsTrending, setReelsTrending] = useState([]);
  const [livestreams, setLivestreams] = useState([]);
  const [teamMeetings, setTeamMeetings] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [spaMarkets, setSpaMarkets] = useState([]);
  const [thongDiep, setThongDiep] = useState(null);
  const [grouped, setGrouped] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const accent = useMemo(() => {
    const d = new Date(selectedDate);
    return DAY_PALETTE[(isNaN(d.getTime()) ? 0 : d.getDay()) % DAY_PALETTE.length];
  }, [selectedDate]);

  const loadData = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    const d = new Date(selectedDate);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    const [dailyRep, salesRes, reelsRes, reelsTrendRes, liveRes, meetingRes, marketRes, spaRes, thongDiepRes, groupedRes] = await Promise.all([
      DailyReportAPI.getList(accessToken, { month, year }),
      DailySalesAPI.getSummary(accessToken, selectedDate),
      ReelsAutoCheckAPI.getList(accessToken, { month, year }),
      ReelTrendingAPI.getList(accessToken, { month, year }),
      LivestreamAPI.getList(accessToken, { month, year }),
      TeamMeetingAPI.getList(accessToken, { month, year }),
      MarketAPI.getList(accessToken, { month, year }),
      SpaMarketAPI.getList(accessToken, { month, year }),
      ThongDiepAPI.getRandom(accessToken),
      ThongDiepAPI.getGrouped(accessToken),
    ]);
    const pickRows = (res) => { if (!res?.success) return []; const data = res.data; if (Array.isArray(data)) return data; return data?.rows || data?.items || []; };
    const filterByDate = (rows) => rows.filter((r) => itemDateStr(r) === selectedDate);
    // Chỉ lấy các mục đã được công ty duyệt — đơn lẻ/sỉ không có approval nên không đụng
    const approvedOnly = (rows) => rows.filter((r) => r.companyApproved === 'approved');
    const drRows = pickRows(dailyRep);
    const reportToday = drRows.find((r) => itemDateStr(r) === selectedDate);
    setChecklistItems(parseChecklist(reportToday?.checklist).filter((t) => t.done));
    setSales(salesRes?.success && salesRes.data ? salesRes.data : null);
    setReelsChecked(filterByDate(approvedOnly(pickRows(reelsRes))));
    setReelsTrending(filterByDate(approvedOnly(pickRows(reelsTrendRes))));
    setLivestreams(filterByDate(approvedOnly(pickRows(liveRes))));
    setTeamMeetings(filterByDate(approvedOnly(pickRows(meetingRes))));
    setMarkets(filterByDate(approvedOnly(pickRows(marketRes))));
    setSpaMarkets(filterByDate(approvedOnly(pickRows(spaRes))));
    setThongDiep(thongDiepRes?.success ? thongDiepRes.data : null);
    setGrouped(groupedRes?.success ? (groupedRes.data || []) : []);
    setLoading(false);
  }, [accessToken, selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const reloadThongDiep = async () => { const res = await ThongDiepAPI.getRandom(accessToken); if (res?.success) setThongDiep(res.data); };

  const displayName = userInfo?.facebook || userInfo?.name || userInfo?.code || 'Bạn';
  const avatarUrl = userInfo?.avatar;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#060d1a' }}>
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Khoe thành tích" onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 w-full max-w-md mx-auto px-3 py-3 space-y-3">

        {/* ── HERO ── */}
        <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(160deg, #0d1f35 0%, #162840 100%)', boxShadow: `0 0 0 1.5px ${accent}66, 0 8px 32px #00000060` }}>
          {/* Accent stripe top */}
          <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${accent}, #9333ea, ${accent})` }} />

          <div className="p-4 flex gap-4 items-start">
            {/* Avatar lớn vuông có glow */}
            <div className="flex-shrink-0" style={{ padding: 2.5, background: `linear-gradient(135deg, ${accent}, #9333ea)`, borderRadius: 18 }}>
              <div className="w-28 h-28 rounded-2xl overflow-hidden bg-gray-800 flex items-center justify-center">
                {avatarUrl
                  ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                  : <span className="text-4xl font-black text-white">{(displayName[0] || 'B').toUpperCase()}</span>}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 pt-1">
              <div className="text-[9px] font-black uppercase tracking-widest mb-1"
                style={{ color: accent, textShadow: `0 0 12px ${accent}88`, opacity: 0.85 }}>
                ✦ Yody Phương Anh ghi nhận thành tích
              </div>

              {/* Tên nổi bật */}
              <div className="text-2xl font-black leading-tight text-white"
                style={{ textShadow: `0 0 20px ${accent}, 0 0 50px ${accent}66` }}>
                {displayName}
              </div>

              {/* Date picker ngay dưới tên */}
              <label className="flex items-center gap-1.5 mt-2 cursor-pointer" style={{ color: `${accent}cc` }}>
                <Calendar size={13} />
                <input type="date" value={selectedDate} max={today}
                  onChange={(e) => { if (e.target.value) setSelectedDate(e.target.value); }}
                  className="text-xs font-semibold outline-none bg-transparent"
                  style={{ color: `${accent}cc`, colorScheme: 'dark' }} />
              </label>
            </div>
          </div>
        </div>

        {loading && (
          <div className="rounded-2xl p-8 text-center text-sm text-white/50" style={{ background: '#0d1f35' }}>
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3" style={{ borderColor: `${accent} ${accent} ${accent} transparent` }} />
            Đang tổng hợp thành tích...
          </div>
        )}

        {!loading && (<>

          {/* ── ĐƠN LẺ ── */}
          {sales?.soLe?.count > 0 && (
            <Block accent={accent} label={`Đơn Lẻ ${displayName} vừa bán được`}>
              <div className="text-center mb-3">
                <div className="flex items-end justify-center gap-2">
                  <span className="font-black text-white leading-none" style={{ fontSize: 80, lineHeight: 1, textShadow: `0 0 40px ${accent}` }}>
                    {sales.soLe.totalQty}
                  </span>
                  <span className="text-3xl font-black mb-2" style={{ color: accent }}>SP</span>
                </div>
                <div className="text-2xl font-black text-white mt-1">{fmtVNDFull(sales.soLe.totalAmount)}</div>
              </div>
              {sales.soLe.productBreakdown && sales.soLe.productBreakdown.length > 0 && (
                <div className="rounded-xl px-3 py-2.5 mt-1" style={{ background: '#ffffff0d', border: `1px solid ${accent}33` }}>
                  <div className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: accent }}>Sản phẩm</div>
                  <p className="text-xs text-white/70 leading-relaxed">
                    {(() => {
                      const items = sales.soLe.productBreakdown;
                      const MAX = 4;
                      const head = items.slice(0, MAX);
                      const rest = items.length - MAX;
                      const headStr = head.map((it) => `${it.qty} ${it.displayName || it.fullName || '?'}`).join(' + ');
                      return rest > 0 ? `${headStr} + ${rest} SP khác` : headStr;
                    })()}
                  </p>
                </div>
              )}
            </Block>
          )}

          {/* ── ĐƠN SỈ ── */}
          {sales?.soSi?.count > 0 && (
            <Block accent={accent} label={`Đơn sỉ hôm nay của ${displayName}`}>
              <div className="text-center">
                <div className="flex items-end justify-center gap-2">
                  <span className="font-black text-white leading-none" style={{ fontSize: 80, lineHeight: 1, textShadow: `0 0 40px ${accent}` }}>
                    {sales.soSi.totalQty}
                  </span>
                  <span className="text-3xl font-black mb-2" style={{ color: accent }}>SP</span>
                </div>
                <div className="text-2xl font-black text-white mt-1">{fmtVNDFull(sales.soSi.totalAmount)}</div>
              </div>
            </Block>
          )}

          {/* ── REELS ── */}
          {reelsChecked.map((r) => (
            <Block key={r.id} accent={accent} label="Reel đã đăng">
              <div className="grid grid-cols-2 gap-3 items-start">
                <GlowPhoto src={r.thumbnailUrl} accent={accent} className="w-full object-cover" style={{ borderRadius: 12, aspectRatio: '9/16', maxHeight: 200 }} />
                <div>
                  {r.time && <div className="text-xs text-white/40 mb-2">{fmtTime(r.time)}</div>}
                  {r.text && <p className="text-sm text-white/90 leading-relaxed line-clamp-8">{r.text}</p>}
                </div>
              </div>
            </Block>
          ))}

          {/* ── REEL XU HƯỚNG ── */}
          {reelsTrending.map((r) => (
            <Block key={r.id} accent={accent} label="Reel xu hướng">
              <div className="grid grid-cols-2 gap-3 items-center">
                <GlowPhoto src={r.proofImage} accent={accent} className="w-full object-cover rounded-xl" />
                <div className="text-center">
                  {r.viewCount > 0 && <>
                    <div className="font-black text-white" style={{ fontSize: 48, lineHeight: 1, textShadow: `0 0 30px ${accent}` }}>
                      {r.viewCount >= 1000 ? `${(r.viewCount / 1000).toFixed(0)}K` : r.viewCount}
                    </div>
                    <div className="text-sm font-bold mt-1" style={{ color: accent }}>Lượt xem</div>
                  </>}
                  {!r.viewCount && <div className="text-sm text-white/50 italic">Reel xu hướng</div>}
                </div>
              </div>
            </Block>
          ))}

          {/* ── LIVESTREAM ── */}
          {livestreams.map((l) => (
            <Block key={l.id} accent={accent} label="Hoàn thành Livestream">
              <div className="grid grid-cols-2 gap-3 items-start">
                <GlowPhoto src={l.productPhoto || l.dateProofImage} accent={accent} className="w-full object-cover rounded-xl" />
                <div className="flex flex-col gap-2 justify-center">
                  {l.channel && (
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-white/40 mb-0.5">Kênh</div>
                      <div className="text-xl font-black text-white">{l.channel}</div>
                    </div>
                  )}
                  {l.views > 0 && (
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-white/40 mb-0.5">Lượt xem</div>
                      <div className="text-lg font-black" style={{ color: accent }}>
                        {l.views >= 1000 ? `${(l.views / 1000).toFixed(1)}K` : l.views}
                      </div>
                    </div>
                  )}
                  {(l.createdAt || l.date) && (
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-white/40 mb-0.5">Thời gian</div>
                      <div className="text-sm font-bold text-white/70">{fmtDMY(l.date || toDateStr(l.createdAt))}</div>
                    </div>
                  )}
                </div>
              </div>
            </Block>
          ))}

          {/* ── THỊ TRƯỜNG THƯỜNG ── */}
          {markets.map((m) => (
            <Block key={m.id} accent={accent} label="Hình ảnh chiến đấu thị trường">
              {/* Tỉnh / Khu vực to */}
              {m.region && (
                <div className="text-center mb-3">
                  <div className="text-xl font-black text-white">{m.region}</div>
                  {m.location && <div className="text-sm font-bold mt-0.5" style={{ color: accent }}>{m.location}</div>}
                </div>
              )}
              {/* 2 ảnh */}
              {(m.startPhoto || m.endPhoto) && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    {m.startPhoto && <GlowPhoto src={m.startPhoto} accent={accent} className="w-full object-cover rounded-xl" />}
                    <div className="text-[9px] text-white/40 text-center mt-1 uppercase tracking-wide">Bắt đầu</div>
                  </div>
                  <div>
                    {m.endPhoto && <GlowPhoto src={m.endPhoto} accent={accent} className="w-full object-cover rounded-xl" />}
                    <div className="text-[9px] text-white/40 text-center mt-1 uppercase tracking-wide">Kết thúc</div>
                  </div>
                </div>
              )}
              {m.revenue > 0 && (
                <div className="rounded-xl py-3 text-center" style={{ background: `${accent}22`, border: `1px solid ${accent}44` }}>
                  <div className="text-2xl font-black text-white">{fmtVNDFull(m.revenue)}</div>
                  <div className="text-xs text-white/50 mt-0.5">Doanh thu</div>
                </div>
              )}
            </Block>
          ))}

          {/* ── THỊ TRƯỜNG SPA ── */}
          {spaMarkets.map((s) => (
            <Block key={s.id} accent={accent} label="Hình ảnh chiến đấu Spa">
              {s.region && (
                <div className="text-center mb-3">
                  <div className="text-xl font-black text-white">{s.region}</div>
                  {(s.spaName || s.location) && <div className="text-sm font-bold mt-0.5" style={{ color: accent }}>{s.spaName || s.location}</div>}
                </div>
              )}
              {(s.spaImage || s.spaProductPhoto) && (
                <div className="mb-3">
                  <GlowPhoto src={s.spaImage || s.spaProductPhoto} accent={accent} className="w-full object-cover rounded-xl max-h-56" />
                </div>
              )}
              {s.soldProducts > 0 && (
                <div className="rounded-xl py-3 text-center" style={{ background: `${accent}22`, border: `1px solid ${accent}44` }}>
                  <div className="font-black text-white" style={{ fontSize: 48, lineHeight: 1, textShadow: `0 0 30px ${accent}` }}>{s.soldProducts}</div>
                  <div className="text-sm font-bold mt-1" style={{ color: accent }}>SP bán được</div>
                </div>
              )}
            </Block>
          ))}

          {/* ── HỌP TEAM ── */}
          {teamMeetings.map((m) => (
            <Block key={m.id} accent={accent} label="Hình ảnh chiến đấu cùng đồng đội">
              {m.meetingType && (
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 rounded-xl py-2.5 text-center font-black text-sm text-white" style={{ background: `${accent}33`, border: `1px solid ${accent}66` }}>
                    {m.meetingType.toUpperCase()}
                  </div>
                </div>
              )}
              {m.proofImage
                ? <GlowPhoto src={m.proofImage} accent={accent} className="w-full object-cover rounded-xl" />
                : <div className="rounded-xl aspect-video flex items-center justify-center text-4xl" style={{ background: '#ffffff08' }}>🤝</div>
              }
            </Block>
          ))}

          {/* ── THÔNG ĐIỆP ── */}
          {thongDiep && (
            <Block accent={accent} label={`Thông điệp của ${displayName}`}>
              <div className="relative">
                {/* Buttons góc trên phải */}
                <div className="absolute top-0 right-0 flex gap-1.5">
                  <button onClick={reloadThongDiep}
                    className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg text-white"
                    style={{ background: `${accent}44`, border: `1px solid ${accent}66` }}>
                    <Shuffle size={11} /> Đổi
                  </button>
                  <button onClick={() => setModalOpen(true)}
                    className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg text-white"
                    style={{ background: '#ffffff11', border: '1px solid #ffffff22' }}>
                    <List size={11} /> Chọn
                  </button>
                </div>

                {/* Quote lớn nổi bật */}
                <div className="pt-8 pb-2 text-center">
                  <div className="text-[28px] leading-snug font-black italic text-white" style={{ textShadow: `0 0 30px ${accent}60` }}>
                    "{thongDiep.cauTrietLy}"
                  </div>
                  {thongDiep.nhomChuDe && (
                    <div className="text-[10px] font-bold uppercase tracking-widest mt-3" style={{ color: `${accent}99` }}>
                      — {thongDiep.nhomChuDe}
                    </div>
                  )}
                </div>
              </div>
            </Block>
          )}

          {/* ── VIỆC ĐÃ HOÀN THÀNH ── */}
          {checklistItems.length > 0 && (
            <Block accent={accent} label="Việc đã hoàn thành">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {checklistItems.map((t, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-white/20 font-bold">|</span>}
                    <span className="text-sm text-white/80 flex items-center gap-1">
                      <span style={{ color: accent }} className="font-black">✓</span>
                      {t.text || t.label || ''}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </Block>
          )}

          {/* ── FOOTER ── */}
          <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d1f35, #162840)', boxShadow: `0 0 0 1px ${accent}44, 0 0 30px ${accent}22` }}>
            <div className="h-1" style={{ background: `linear-gradient(90deg, transparent, ${accent}, #9333ea, ${accent}, transparent)` }} />
            <div className="px-4 py-5 text-center">
              <div className="text-[9px] font-black uppercase tracking-widest text-white/30 mb-2">✨ Yody Phương Anh</div>
              <div className="text-xl font-black mb-1" style={{ color: accent, textShadow: `0 0 20px ${accent}` }}>
                Ghi nhận thành tích xuất sắc
              </div>
              <div className="text-base font-black text-white/90" style={{ textShadow: `0 0 15px ${accent}66` }}>
                của <span style={{ color: accent }}>{displayName}</span> hôm nay
              </div>
              <div className="text-xs text-white/30 mt-2 font-medium">💪 Tiếp tục tỏa sáng mỗi ngày!</div>
            </div>
            <div className="h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${accent}66, transparent)` }} />
          </div>

        </>)}
      </div>

      <ThongDiepModal visible={modalOpen} onClose={() => setModalOpen(false)} accent={accent} groups={grouped}
        onPick={(it) => { setThongDiep(it); setModalOpen(false); }} />
    </div>
  );
};

export default KhoeThanhTichPage;
